import {
	action,
	computed,
	makeObservable,
	observable,
	override,
	runInAction
} from 'mobx';
import { EntityStore } from '../../../shared/stores/EntityStore';
import { api } from '../../../shared/api/api';
import { buildFormData } from 'ats/utils/buildFormData';
import { industryStore } from 'components/settings/industries/IndustryStore';

class ClientStore extends EntityStore {
	constructor() {
		super('clients');
		makeObservable(this);
	}

	@observable clients = [];

	defaultTableColumnsOptions = [
		{ value: 'name', label: 'Name' },
		{ value: 'industry', label: 'Industry' },
		{ value: 'size', label: 'Size' },
		{ value: 'accountManager', label: 'Account Manager' },
		{ value: 'primaryContact', label: 'Primary Contact' },
		{ value: 'openJobs', label: 'Open Jobs' },
		{ value: 'openVacancies', label: 'Open Vacancies' },
		{ value: 'daysOpenAvg', label: 'Days Open Avg' }
	];

	tableColumnsOptions = [
		{ value: 'name', label: 'Name' },
		{ value: 'industry', label: 'Industry' },
		{ value: 'size', label: 'Size' },
		{ value: 'accountManager', label: 'Account Manager' },
		{ value: 'primaryContact', label: 'Primary Contact' },
		{ value: 'openJobs', label: 'Open Jobs' },
		{ value: 'openVacancies', label: 'Open Vacancies' },
		{ value: 'daysOpenAvg', label: 'Days Open Avg' },
		{ value: 'createdAt', label: 'Created At' },
		{ value: 'updatedAt', label: 'Updated At' }
	];

	@computed
	get industryOptions() {
		return industryStore.industryOptions;
	}

	@action
	async loadClients(reset = false) {
		const result = await this.search(reset);
		runInAction(() => {
			this.clients = result.data.rows;
		});
	}

	@action
	async getClients(query = {}) {
		const url = this.entityPluralEndpoint + '/query';
		const pagination =
			query && query.pagination
				? query.pagination
				: {
						page: 1,
						limit: 20
					};

		const result = await api.post(url, {
			query: {
				...query,
				pagination
			}
		});

		return result;
	}

	@override
	async create(request) {
		if (request.profileImage) {
			const config = {
				headers: {
					'content-type': 'multipart/form-data'
				}
			};
			const formData = new FormData();

			Object.keys(request).forEach(key => {
				if (request[key] !== null && request[key] !== undefined) {
					formData.append(key, request[key]);
				}
			});

			const result = await api.post(
				this.entityPluralEndpoint,
				formData,
				config
			);
			return result;
		} else {
			return super.create(request);
		}
	}

	@override
	async update(id, request) {
		if (
			request.client.profileImage &&
			request.client.profileImage instanceof File
		) {
			const config = {
				headers: {
					'content-type': 'multipart/form-data'
				}
			};
			const formData = new FormData();

			buildFormData(formData, request);

			const result = await api.put(
				`${this.entityPluralEndpoint}/${id}`,
				formData,
				config
			);
			return result;
		} else {
			return super.update(id, request);
		}
	}

	@action
	async uploadClientImage(clientId, imageFile) {
		const config = {
			headers: {
				'content-type': 'multipart/form-data'
			}
		};
		const formData = new FormData();
		formData.append('image', imageFile);
		const result = await api.put(
			`${this.entityPluralEndpoint}/${clientId}/image`,
			formData,
			config
		);
		return result;
	}

	@action
	async getClientContacts(clientId) {
		const result = await api.get(
			`${this.entityPluralEndpoint}/${clientId}/client-contacts`
		);
		return result;
	}

	@action
	async createClientContact(clientId, clientContact) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/${clientId}/client-contacts`,
			{ clientContact }
		);
		return result;
	}

	@action
	async updateClientContact(clientId, contactId, clientContact) {
		const result = await api.put(
			`${this.entityPluralEndpoint}/${clientId}/client-contacts/${contactId}`,
			{ clientContact }
		);
		return result;
	}

	@action
	async bulkDelete(ids) {
		const result = await api.post(`${this.entityPluralEndpoint}/bulk/delete`, {
			ids
		});
		return result;
	}
}

export const clientStore = new ClientStore();
